"use client"

import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import Link from "next/link"
import { isChunkLoadError, recoverFromChunkLoadError } from "@/lib/utils/chunk-error"

// Global error boundary for the app router.
// Keeps the UI friendly and points to likely config issues (e.g., missing env vars).
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const isStaleBundle = isChunkLoadError(error)
  const [isReloading, setIsReloading] = useState(isStaleBundle)

  // A missing chunk means this tab is running an older build than the server.
  // Reload once to pick up the current one instead of showing an error screen.
  useEffect(() => {
    if (!isStaleBundle) return
    if (!recoverFromChunkLoadError()) {
      // Already tried recently - fall through to the normal error UI.
      setIsReloading(false)
    }
  }, [isStaleBundle])

  if (isReloading) {
    return (
      <div className="min-h-[60vh] bg-background flex items-center justify-center px-4">
        <p className="text-sm text-muted-foreground">Updating to the latest version…</p>
      </div>
    )
  }

  const message = isStaleBundle
    ? "A newer version of Planami is available, but this page could not refresh automatically. Please reload the page."
    : error?.message ||
      "Something went wrong. Please retry, refresh page or contact support if this continues."

  return (
    <div className="min-h-[60vh] bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-2xl border rounded-lg p-8 shadow-sm bg-card space-y-4">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold">{isStaleBundle ? "Please reload" : "We hit a snag"}</h1>
          <p className="text-sm text-muted-foreground">
            {isStaleBundle
              ? "Your planner data is safe — this page just needs a refresh."
              : "The app ran into an unexpected error."}
          </p>
        </div>

        <div className="rounded-md bg-muted/60 p-4 text-sm text-muted-foreground break-words">
          {message}
          {error?.digest ? <div className="mt-2 text-xs opacity-70">Digest: {error.digest}</div> : null}
        </div>

        <div className="flex flex-wrap gap-3">
          {isStaleBundle ? (
            <Button onClick={() => window.location.reload()}>Reload page</Button>
          ) : (
            <Button onClick={reset}>Try again</Button>
          )}
          <Button variant="outline" asChild>
            <Link href="/">Go home</Link>
          </Button>
          <Button variant="outline" asChild>
            <a
              href={`mailto:support@planami.app?subject=Bug Report&body=${encodeURIComponent(`Hi,\n\nI ran into an error:\n\n${message}${error?.digest ? `\n\nDigest: ${error.digest}` : ""}\n\nSteps to reproduce:\n`)}`}
            >
              Report a bug
            </a>
          </Button>
        </div>
      </div>
    </div>
  )
}

